import {
  ResourceCacheEvents,
  resourceCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/resourceCacheRegister';
import {
  getKeyParamsForInvalidation,
  getQueryConfig,
} from '@monorepo/shared/utils/queryUtils';
import { ResourceResponse } from 'mapistry-shared';
import { queryCache, QueryConfig, useQuery } from 'react-query';
import { Api } from '../../apiClient';

type KeyPartsAllDefined = [string, string, string, string];
type KeyPartsPartial = [
  string,
  string | undefined,
  string | undefined,
  string | undefined,
];

type KeyParams = Api.FetchResourceParams;
const KEY_START = 'org-resource';

function getKeyPartsInOrder(keyParams: KeyParams): KeyPartsAllDefined;
function getKeyPartsInOrder(keyParams: Partial<KeyParams>): KeyPartsPartial;
function getKeyPartsInOrder(
  keyParams: KeyParams | Partial<KeyParams>,
): KeyPartsAllDefined | KeyPartsPartial {
  return [
    KEY_START,
    keyParams.organizationId,
    keyParams.resourceTypeId,
    keyParams.resourceId,
  ];
}

export const createKey = (keyParams: KeyParams) =>
  getKeyPartsInOrder(keyParams);

type Fetcher = Api.DataHookQueryFn<typeof createKey, typeof Api.fetchResource>;

const fetcher: Fetcher = (_, organizationId, resourceTypeId, resourceId) =>
  Api.fetchResource({ organizationId, resourceTypeId, resourceId });

type UseResourceParams = Partial<Api.FetchResourceParams> & {
  config?: QueryConfig<ResourceResponse, Api.ErrorResponse>;
};

export function useResource({
  config: inputConfig,
  organizationId,
  resourceTypeId,
  resourceId,
}: UseResourceParams) {
  const isEnabled = !!organizationId && !!resourceTypeId && !!resourceId;
  const config = getQueryConfig(inputConfig, isEnabled);
  const key = isEnabled
    ? createKey({ organizationId, resourceTypeId, resourceId })
    : undefined;

  const { data, ...queryInfo } = useQuery(key, fetcher, config);
  return {
    ...queryInfo,
    resource: data,
  };
}

type QueryCache = ReturnType<typeof useResource>['resource'];
type NonEmptyQueryCache = Exclude<QueryCache, undefined>;

export function getCache(keyParams: KeyParams): QueryCache {
  return queryCache.getQueryData<QueryCache>(createKey(keyParams));
}

export function setCache(keyParams: KeyParams, item: NonEmptyQueryCache): void {
  queryCache.setQueryData(createKey(keyParams), item);
}

type InvalidateCacheParams = Omit<KeyParams, 'resourceId'> &
  Partial<Pick<KeyParams, 'resourceId'>>;
export async function invalidateCache(
  keyParams: InvalidateCacheParams,
): Promise<void> {
  await queryCache.invalidateQueries(
    getKeyParamsForInvalidation(getKeyPartsInOrder(keyParams)),
  );
}

resourceCacheRegister([ResourceCacheEvents.DELETE], {
  hookName: 'useResource',
  callback: (keyParams: InvalidateCacheParams) => invalidateCache(keyParams),
});
